import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import type { CasePriority, CaseStatus, CaseSummary, FeeType } from '../types';

const LEGAL_AREAS: { value: string; key: string }[] = [
  { value: 'דיני משפחה', key: 'family' },
  { value: 'נדל״ן', key: 'real_estate' },
  { value: 'דיני עבודה', key: 'labor' },
  { value: 'מסחרי', key: 'commercial' },
  { value: 'פלילי', key: 'criminal' },
  { value: 'ירושה', key: 'inheritance' },
  { value: 'נזיקין', key: 'tort' },
  { value: 'אחר', key: 'other' },
];

const FEE_TYPES: FeeType[] = ['hourly', 'fixed', 'contingency'];
const PRIORITIES: CasePriority[] = ['low', 'medium', 'high', 'urgent'];
const STATUSES: CaseStatus[] = ['open', 'pending', 'closed', 'archived'];

export function CaseEditModal({
  initial,
  onSaved,
  onClose,
}: {
  initial: CaseSummary;
  onSaved: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['cases', 'common']);

  const [area, setArea] = useState(initial.area ?? '');
  const [status, setStatus] = useState<CaseStatus>(initial.status);
  const [feeType, setFeeType] = useState<FeeType | ''>(initial.feeType ?? '');
  const [hourlyRate, setHourlyRate] = useState(
    initial.hourlyRateIls != null ? String(initial.hourlyRateIls) : '',
  );
  const [procedureType, setProcedureType] = useState(initial.procedureType ?? '');
  const [court, setCourt] = useState(initial.court ?? '');
  const [dueAt, setDueAt] = useState(initial.dueAt ? toDateInput(initial.dueAt) : '');
  const [priority, setPriority] = useState<CasePriority>(initial.priority);
  const [description, setDescription] = useState(initial.description ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        area: area.trim() || null,
        status,
        feeType: feeType || null,
        hourlyRateIls: hourlyRate ? Number(hourlyRate) : null,
        procedureType: procedureType.trim() || null,
        court: court.trim() || null,
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
        priority,
        description: description.trim() || null,
      };
      await apiFetch(`/api/cases/${initial.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : err instanceof Error ? err.message : 'unknown');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="form-card-header">
            <div style={{ flex: 1 }}>
              <div className="form-preview-name">{t('form.title_edit')}</div>
              <div className="form-preview-type">{initial.caseNumber}</div>
            </div>
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label={t('common:actions.close')}
            >
              ×
            </button>
          </div>

          <div className="form-section">
            <div className="field-grid">
              <div className="field">
                <label>{t('form.field_area')}</label>
                <select value={area} onChange={(e) => setArea(e.target.value)}>
                  <option value="">{t('common:actions.select')}</option>
                  {LEGAL_AREAS.map((a) => (
                    <option key={a.value} value={a.value}>
                      {t(`areas.${a.key}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>{t('detail.field_status')}</label>
                <select value={status} onChange={(e) => setStatus(e.target.value as CaseStatus)}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {t(`statuses.${s}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>{t('form.field_procedure_type')}</label>
                <input
                  value={procedureType}
                  onChange={(e) => setProcedureType(e.target.value)}
                  placeholder={t('form.field_procedure_type_placeholder')}
                />
              </div>
              <div className="field">
                <label>{t('form.field_court')}</label>
                <input
                  value={court}
                  onChange={(e) => setCourt(e.target.value)}
                  placeholder={t('form.field_court_placeholder')}
                />
              </div>
              <div className="field">
                <label>{t('form.field_priority')}</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as CasePriority)}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {t(`priorities.${p}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>{t('form.field_due_at')}</label>
                <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
              </div>
              <div className="field">
                <label>{t('form.field_fee_type')}</label>
                <select value={feeType} onChange={(e) => setFeeType(e.target.value as FeeType | '')}>
                  <option value="">{t('common:actions.select')}</option>
                  {FEE_TYPES.map((f) => (
                    <option key={f} value={f}>
                      {t(`fee_types.${f}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>{t('form.field_hourly_rate')}</label>
                <input
                  type="number"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  placeholder={t('form.field_hourly_rate_placeholder')}
                  min={0}
                  step={50}
                />
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>{t('form.field_description')}</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  placeholder={t('form.field_description_placeholder')}
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="form-section">
              <div className="signin-error">{error}</div>
            </div>
          )}

          <div className="form-footer">
            <button type="submit" className="btn-save" disabled={busy}>
              {busy ? t('common:actions.loading_dots') : t('common:actions.save')}
            </button>
            <button type="button" className="btn-cancel" onClick={onClose} disabled={busy}>
              {t('common:actions.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function toDateInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
